import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "../css/header.css";
import logo from "../assets/logo2.jpeg";
import washing_machine_logo from "../assets2/washing_machine_logo.png";

const Header = () => {
  const [navOpen, setNavOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const panelRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current page is Washing Machine Service
  const isWashingMachinePage = location.pathname === "/washing-machine-service";

  // Single source of truth for dynamic page data
  const currentLogo = isWashingMachinePage ? washing_machine_logo : logo;
  const logoSiteName = isWashingMachinePage ? "Washing Machine Service" : "Jay Ambe AC Service";
  
  // Dynamic Phone Number assignment based on path location
  const currentPhoneNumber = isWashingMachinePage ? "73175 96984" : "72378 98275";
  const rawPhoneNumber = isWashingMachinePage ? "7317596984" : "7237898275";

  // single source of truth for services
  const servicesList = [
    { title: "AC Service", to: "/service" },
    { title: "AC Repair", to: "/acrepair" },
    { title: "AC Installation", to: "/acinstallation" },
    { title: "AC Uninstallation Service", to: "/acunstill" },
    { title: "AC Gas Refill Service", to: "/acgais" },
    { title: "AC Annual Maintenance Service", to: "/acmaintain" },
    { title: "Split AC Repair and Service", to: "/splitac" },
    { title: "Window AC Repair and Service", to: "/windos" },
    { title: "Cassette AC Repair And Service", to: "/cassetteAC" },
  ];

  // Close on outside click or Esc
  useEffect(() => {
    const onDocClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && navOpen) {
        setNavOpen(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && servicesOpen) {
        setServicesOpen(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") {
        setNavOpen(false);
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [navOpen, servicesOpen]);

  // ensure body padding so header doesn't overlap content
  useEffect(() => {
    const setBodyOffset = () => {
      try {
        const topbarEl = document.getElementById("topbar");
        const headerEl = document.querySelector(".site-header");
        const topbarHeight = topbarEl ? Math.ceil(topbarEl.getBoundingClientRect().height) : 0;
        const headerHeight = headerEl ? Math.ceil(headerEl.getBoundingClientRect().height) : 0;
        const total = topbarHeight + headerHeight;
        document.body.style.paddingTop = `${total}px`;
      } catch (err) {}
    };

    setBodyOffset();
    window.addEventListener("resize", setBodyOffset);
    const id = setTimeout(setBodyOffset, 350);
    return () => {
      window.removeEventListener("resize", setBodyOffset);
      clearTimeout(id);
    };
  }, []);

  // Simple navigation - just go to the page, no hash scrolling
  const handleNavigation = (to) => {
    navigate(to);
    setNavOpen(false);
    setServicesOpen(false);
  };

  const closeNav = () => {
    setNavOpen(false);
    setServicesOpen(false);
  };

  // keyboard handler for Services toggle (accessibility)
  const onServicesKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      setServicesOpen(true);
      setTimeout(() => {
        const first = dropdownRef.current && dropdownRef.current.querySelector(".dropdown-link");
        if (first) first.focus();
      }, 50);
    } else if (e.key === "Escape") {
      setServicesOpen(false);
    }
  };

  return (
    <header className="site-header" role="banner">
      <div className="header-wrapper">
        <div className="header-left">
          <Link 
            to="/" 
            className="logo-link" 
            aria-label={logoSiteName} 
            onClick={closeNav}
          >
            <img 
              src={currentLogo} 
              alt={logoSiteName} 
              className="site-logo-desktop" 
            />
            <img 
              src={currentLogo} 
              alt={logoSiteName} 
              className="site-logo-mobile" 
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="header-nav desktop-only" aria-label="Primary Navigation">
          <ul className="nav-list">
            <li>
              <Link to="/" className="nav-link">
                Home
              </Link>
            </li>
            <li>
              <Link to="/about" className="nav-link">
                About
              </Link>
            </li>

            {/* Services dropdown (desktop) */}
            <li
              className={`nav-item nav-dropdown ${servicesOpen ? "open" : ""}`}
              ref={dropdownRef}
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                className="nav-link nav-dropdown-toggle"
                aria-haspopup="true"
                aria-expanded={servicesOpen}
                onClick={(e) => {
                  e.preventDefault();
                  setServicesOpen((s) => !s);
                }}
                onKeyDown={onServicesKeyDown}
              >
                Services ▾
              </button>

              <ul className={`dropdown-list ${servicesOpen ? "show" : ""}`} role="menu" aria-hidden={!servicesOpen}>
                {servicesList.map((s) => (
                  <li key={s.to} role="none">
                    <Link
                      to={s.to}
                      className="dropdown-link"
                      role="menuitem"
                      onClick={() => setServicesOpen(false)}
                    >
                      {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
            <li>
              <Link to="/washing-machine-service" className="nav-link">
                Washing Machine Service
              </Link>
            </li>
            <li>
              <Link to="/privacy" className="nav-link">
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link to="/contact" className="nav-link">
                Contact
              </Link>
            </li>
            
          </ul>
        </nav>

        <div className="header-right desktop-only">
          <a className="header-phone" href={`tel:${rawPhoneNumber}`}>{currentPhoneNumber}</a>
        </div>

        {/* Hamburger for mobile */}
        <button
          className={`hamburger ${navOpen ? "is-active" : ""}`}
          aria-expanded={navOpen}
          aria-controls="mobile-panel"
          aria-label={navOpen ? "Close menu" : "Open menu"}
          onClick={() => setNavOpen((s) => !s)}
        >
          <span className="hamburger-box">
            <span className="hamburger-inner" />
          </span>
        </button>
      </div>

      {/* Mobile panel overlay */}
      <div className={`mobile-panel-overlay ${navOpen ? "open" : ""}`} aria-hidden={!navOpen}>
        <div className="mobile-panel" id="mobile-panel" ref={panelRef} role="dialog" aria-modal="true">
          <div className="mobile-panel-top">
            <Link 
              to="/" 
              className="mobile-logo-link" 
              onClick={() => { 
                navigate("/"); 
                setNavOpen(false); 
              }}
            >
              <img src={currentLogo} alt={logoSiteName} className="site-logo-mobile" />
            </Link>
            <button className="mobile-close" aria-label="Close menu" onClick={closeNav}>✕</button>
          </div>

          {/* NAV LINKS (top) */}
          <nav className="mobile-nav-body" aria-label="Mobile Navigation">
            <ul className="mobile-list">
              <li className="mobile-item">
                <button className="mobile-link" onClick={() => handleNavigation("/")}>
                  Home
                </button>
              </li>
              <li className="mobile-item">
                <button className="mobile-link" onClick={() => handleNavigation("/about")}>
                  About
                </button>
              </li>
              <li className="mobile-item">
                <button className="mobile-link" onClick={() => handleNavigation("/washing-machine-service")}>
                  Washing Machine Service
                </button>
              </li>
              <li className="mobile-item">
                <button className="mobile-link" onClick={() => handleNavigation("/privacy")}>
                  Privacy Policy
                </button>
              </li>
              
              <li className="mobile-item">
                <button className="mobile-link" onClick={() => handleNavigation("/contact")}>
                  Contact
                </button>
              </li>
            </ul>

            {/* SEPARATE "OUR SERVICES" SECTION (MOBILE) */}
            <div className="mobile-services-section" aria-labelledby="mobile-services-heading">
              <h3 id="mobile-services-heading" className="mobile-services-title">Our Services</h3>

              <ul className="mobile-services-list">
                {servicesList.map((s) => (
                  <li key={s.to} className="mobile-services-item">
                    <button className="mobile-services-link" onClick={() => handleNavigation(s.to)}>
                      {s.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          {/* footer phone CTA */}
          <div className="mobile-panel-footer">
            <a className="mobile-phone" href={`tel:${rawPhoneNumber}`} onClick={() => setNavOpen(false)}>
              CALL US @ {currentPhoneNumber}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;